import { Box, SimpleGrid, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getDemographicColor } from '../../demographics';
import type { TccData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function ParticipantsCard({ data }: { data: TccData }) {
  const theme = useMantineTheme();
  const series = data.participants;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const goalAllTime = 60;
  const allTime = data.participantsAllTimeTotal;

  const qoq = cur ? pctChange(cur.participantsStarted, prev?.participantsStarted ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.participantsStarted, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.participantsStarted, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: allTime, goal: goalAllTime, direction: 'min' }),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const raceTotals = data.participantsAllTimeByRace;
  const genderTotals = data.participantsAllTimeByGender;
  const totalAllTime = Object.values(raceTotals).reduce((sum, v) => sum + v, 0);

  const raceOrder = Object.entries(raceTotals)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const genderOrder = Object.entries(genderTotals)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  const raceColor = (idx: number) => getDemographicColor(theme, idx);
  const genderColor = (idx: number) => getDemographicColor(theme, idx);

  const qoqShareText = (group: 'byRace' | 'byGender', label: string, curVal: number, prevVal: number | null) => {
    const qoqShare = pctChange(curVal, prevVal);
    if (qoqShare === null) return '— QoQ';
    if (qoqShare === 0) return '0.00% QoQ';
    return `${qoqShare > 0 ? '↑' : '↓'}${Math.abs(qoqShare).toFixed(2)}% QoQ`;
  };

  const seriesTotal = series.reduce((sum, point) => sum + point.participantsStarted, 0);
  const priorParticipants = Math.max(0, allTime - seriesTotal);
  let cumulative = priorParticipants;
  const chartData = series.map((point) => {
    cumulative += point.participantsStarted;
    return {
      ...point,
      cumulative,
    };
  });
  const maxCumulative = Math.max(allTime, ...chartData.map((p) => p.cumulative));

  return (
    <CardShell
      title="Program Participants"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who began participation this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.participantsStarted ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date participants (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: `Total participants across all time (includes periods not shown). Goal: ${goalAllTime} learners.`,
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
              sublabel: `Goal ${goalAllTime}`,
            },
          ]}
        />

        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 700, cols: 1 }]} sx={{ marginTop: '0.75rem' }}>
          <Stack spacing={6}>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2 }}>
              Participants By Race
            </Text>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
              <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const pct = totalAllTime > 0 ? Math.round((p.value / totalAllTime) * 100) : 0;
                        const curRace = cur?.byRace?.[p.name as keyof NonNullable<typeof cur>['byRace']] ?? 0;
                        const prevRace = prev?.byRace?.[p.name as keyof NonNullable<typeof prev>['byRace']] ?? null;
                        return (
                          <div style={tooltipShellStyle as any}>
                            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{p.name}</div>
                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                              <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                              <div style={{ opacity: 0.9 }}>{pct}% of participants (all-time)</div>
                              <div style={{ opacity: 0.9 }}>{qoqShareText('byRace', p.name, curRace, prevRace)}</div>
                            </div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                        border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                        borderRadius: 10,
                        color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        padding: '10px 12px',
                      }}
                      labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                      itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                    />
                    <Pie
                      data={raceOrder.map(([race, value]) => ({ name: race, value }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={1}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {raceOrder.map(([race], idx) => (
                        <Cell key={race} fill={raceColor(idx)} stroke="none" />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: 12,
                        fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        fontWeight: 600,
                      }}
                    >
                      All-Time
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'grid',
                  gridTemplateColumns: raceOrder.length > 4 ? '1fr 1fr' : '1fr',
                  gap: '6px 8px',
                  alignContent: 'center',
                }}
              >
                {raceOrder.map(([race], idx) => (
                  <Box key={race} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: raceColor(idx),
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="xs" color="dimmed">
                      {race}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>

          <Stack spacing={6}>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2 }}>
              Participants By Gender
            </Text>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
              <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const pct = totalAllTime > 0 ? Math.round((p.value / totalAllTime) * 100) : 0;
                        const curGender = cur?.byGender?.[p.name as keyof NonNullable<typeof cur>['byGender']] ?? 0;
                        const prevGender = prev?.byGender?.[p.name as keyof NonNullable<typeof prev>['byGender']] ?? null;
                        return (
                          <div style={tooltipShellStyle as any}>
                            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{p.name}</div>
                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                              <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                              <div style={{ opacity: 0.9 }}>{pct}% of participants (all-time)</div>
                              <div style={{ opacity: 0.9 }}>{qoqShareText('byGender', p.name, curGender, prevGender)}</div>
                            </div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                        border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                        borderRadius: 10,
                        color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        padding: '10px 12px',
                      }}
                      labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                      itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                    />
                    <Pie
                      data={genderOrder.map(([gender, value]) => ({ name: gender, value }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={1}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {genderOrder.map(([gender], idx) => (
                        <Cell key={gender} fill={genderColor(idx)} stroke="none" />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: 12,
                        fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        fontWeight: 600,
                      }}
                    >
                      All-Time
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'grid',
                  gridTemplateColumns: genderOrder.length > 4 ? '1fr 1fr' : '1fr',
                  gap: '6px 8px',
                  alignContent: 'center',
                }}
              >
                {genderOrder.map(([gender], idx) => (
                  <Box key={gender} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: genderColor(idx),
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="xs" color="dimmed">
                      {gender}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </SimpleGrid>

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Participants
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, maxCumulative]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  const v = p?.cumulative;
                  const quarterValue = p?.participantsStarted;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? v : '—'} Total</div>
                        <div style={{ opacity: 0.9 }}>
                          {typeof quarterValue === 'number' ? quarterValue : '—'} started this quarter
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={goalAllTime} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Cumulative Participants"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`tcc-participants-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
